import { useState, useEffect } from 'react';
import { useTelegram } from '../hooks/useTelegram';
import { getAIAgents, createAIAgent, updateAIAgent, deleteAIAgent } from '../utils/api';
import type { AIAgent, AIAgentTier, AIProviderType } from '@tg-miniapp/shared-types';

export function AdminAIPage(): React.JSX.Element {
  const { initData } = useTelegram();
  const [agents, setAgents] = useState<AIAgent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState<string | null>(null);

  const [formData, setFormData] = useState<Omit<AIAgent, 'id'>>({
    name: '',
    provider: 'openai-compatible',
    modelId: '',
    apiKey: '',
    baseUrl: '',
    isActive: true,
    tier: 'ALL'
  });

  useEffect(() => {
    loadAgents();
  }, [initData]);

  async function loadAgents() {
    if (!initData) return;
    try {
      const res = await getAIAgents(initData);
      if (res.ok) setAgents(res.value.agents);
      else setError('Ошибка загрузки агентов');
    } catch (e) {
      setError('Ошибка сети');
    } finally {
      setIsLoading(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!initData) return;
    setIsLoading(true);

    try {
      if (isEditing) {
        const res = await updateAIAgent(initData, isEditing, formData);
        if (res.ok) {
          setAgents(agents.map(a => a.id === isEditing ? res.value.agent : a));
          setIsEditing(null);
        } else {
          setError('Ошибка обновления агента');
        }
      } else {
        const res = await createAIAgent(initData, formData);
        if (res.ok) setAgents([...agents, res.value.agent]);
        else setError('Ошибка создания агента');
      }
      
      setFormData({ name: '', provider: 'openai-compatible', modelId: '', apiKey: '', baseUrl: '', isActive: true, tier: 'ALL' });
    } catch (err) {
      setError('Сетевая ошибка');
    } finally {
      setIsLoading(false);
    }
  }

  async function handleDelete(id: string) {
    if (!initData || !confirm('Удалить агента?')) return;
    setIsLoading(true);
    try {
      const res = await deleteAIAgent(initData, id);
      if (res.ok) setAgents(agents.filter(a => a.id !== id));
      else setError('Ошибка удаления');
    } finally {
      setIsLoading(false);
    }
  }

  function handleEdit(agent: AIAgent) {
    setIsEditing(agent.id);
    setFormData({
      name: agent.name,
      provider: agent.provider,
      modelId: agent.modelId,
      apiKey: agent.apiKey,
      baseUrl: agent.baseUrl || '',
      isActive: agent.isActive,
      tier: agent.tier
    });
  }

  if (isLoading && agents.length === 0) return <div className="p-4 text-center">Загрузка...</div>;

  return (
    <div className="flex flex-col gap-6 animate-in fade-in pb-20">
      <div>
        <h1 className="text-2xl font-bold bg-gradient-to-r from-tg-theme-text to-tg-theme-hint bg-clip-text text-transparent">
          🤖 AI Агенты
        </h1>
        <p className="text-sm text-tg-theme-hint mt-1">
          Настройка провайдеров и моделей для платформы.
        </p>
      </div>

      {error && (
        <div className="bg-red-500/10 text-red-500 p-3 rounded-xl text-sm border border-red-500/20">
          {error}
        </div>
      )}

      {/* Форма создания/редактирования */}
      <div className="bg-tg-theme-bg p-5 rounded-2xl shadow-sm border border-black/5 dark:border-white/5">
        <h2 className="text-lg font-bold mb-4">{isEditing ? 'Редактировать агента' : 'Добавить нового агента'}</h2>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label className="text-xs font-semibold text-tg-theme-hint uppercase tracking-wider mb-1 block">Название (например: Claude Fast)</label>
            <input
              required
              className="w-full bg-black/5 dark:bg-white/5 border border-transparent rounded-xl px-4 py-3 text-sm focus:border-blue-500 outline-none transition-colors"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-tg-theme-hint uppercase tracking-wider mb-1 block">Провайдер</label>
              <select
                className="w-full bg-black/5 dark:bg-white/5 border border-transparent rounded-xl px-4 py-3 text-sm outline-none"
                value={formData.provider}
                onChange={(e) => setFormData({ ...formData, provider: e.target.value as AIProviderType })}
              >
                <option value="claude">Claude (Anthropic)</option>
                <option value="openrouter">OpenRouter</option>
                <option value="openai-compatible">OpenAI Compatible (Groq, DeepSeek)</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-tg-theme-hint uppercase tracking-wider mb-1 block">Доступ (Тариф)</label>
              <select
                className="w-full bg-black/5 dark:bg-white/5 border border-transparent rounded-xl px-4 py-3 text-sm outline-none"
                value={formData.tier}
                onChange={(e) => setFormData({ ...formData, tier: e.target.value as AIAgentTier })}
              >
                <option value="ALL">Всем (Free + Pro)</option>
                <option value="FREE">Только Free</option>
                <option value="PRO">Только PRO</option>
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-tg-theme-hint uppercase tracking-wider mb-1 block">ID Модели</label>
            <input
              required
              placeholder="gpt-4o-mini"
              className="w-full bg-black/5 dark:bg-white/5 border border-transparent rounded-xl px-4 py-3 text-sm outline-none"
              value={formData.modelId}
              onChange={(e) => setFormData({ ...formData, modelId: e.target.value })}
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-tg-theme-hint uppercase tracking-wider mb-1 block">API Ключ</label>
            <input
              required
              type="password"
              placeholder="sk-..."
              className="w-full bg-black/5 dark:bg-white/5 border border-transparent rounded-xl px-4 py-3 text-sm outline-none"
              value={formData.apiKey}
              onChange={(e) => setFormData({ ...formData, apiKey: e.target.value })}
            />
          </div>

          {formData.provider === 'openai-compatible' && (
            <div>
              <label className="text-xs font-semibold text-tg-theme-hint uppercase tracking-wider mb-1 block">Base URL (для кастомных провайдеров)</label>
              <input
                required
                placeholder="https://api.groq.com/openai/v1"
                className="w-full bg-black/5 dark:bg-white/5 border border-transparent rounded-xl px-4 py-3 text-sm outline-none"
                value={formData.baseUrl}
                onChange={(e) => setFormData({ ...formData, baseUrl: e.target.value })}
              />
            </div>
          )}

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="isActive"
              className="w-5 h-5 rounded-lg accent-blue-500"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
            />
            <label htmlFor="isActive" className="text-sm font-medium">Агент активен</label>
          </div>

          <div className="flex gap-3 mt-2">
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 bg-tg-theme-button text-tg-theme-button-text font-semibold py-3 rounded-xl disabled:opacity-50 transition-transform active:scale-95"
            >
              {isEditing ? 'Сохранить изменения' : 'Добавить агента'}
            </button>
            {isEditing && (
              <button
                type="button"
                onClick={() => { setIsEditing(null); setFormData({ name: '', provider: 'openai-compatible', modelId: '', apiKey: '', baseUrl: '', isActive: true, tier: 'ALL' }); }}
                className="px-6 bg-black/5 dark:bg-white/5 font-semibold py-3 rounded-xl"
              >
                Отмена
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Список агентов */}
      <div className="flex flex-col gap-4">
        <h2 className="text-lg font-bold">Активные агенты в KV</h2>
        {agents.length === 0 ? (
          <div className="p-8 text-center bg-black/5 dark:bg-white/5 rounded-2xl border border-dashed border-black/10 dark:border-white/10 text-tg-theme-hint">
            Нет настроенных агентов. Используются системные fallback (env).
          </div>
        ) : (
          agents.map((agent) => (
            <div key={agent.id} className="bg-tg-theme-bg p-5 rounded-2xl shadow-sm border border-black/5 dark:border-white/5 flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-xl">{agent.isActive ? '🟢' : '🔴'}</span>
                  <h3 className="font-bold text-lg">{agent.name}</h3>
                </div>
                <div className="flex gap-2">
                  <span className={`text-[10px] font-bold px-2 py-1 rounded-md uppercase tracking-wide ${agent.tier === 'PRO' ? 'bg-purple-500/10 text-purple-500' : agent.tier === 'FREE' ? 'bg-green-500/10 text-green-500' : 'bg-blue-500/10 text-blue-500'}`}>
                    {agent.tier}
                  </span>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="text-tg-theme-hint">Провайдер:</div>
                <div className="font-medium">{agent.provider}</div>
                <div className="text-tg-theme-hint">Модель:</div>
                <div className="font-medium truncate">{agent.modelId}</div>
                {agent.baseUrl && (
                  <>
                    <div className="text-tg-theme-hint">Base URL:</div>
                    <div className="font-medium truncate text-xs">{agent.baseUrl}</div>
                  </>
                )}
              </div>

              <div className="flex gap-2 mt-2 pt-3 border-t border-black/5 dark:border-white/5">
                <button
                  onClick={() => handleEdit(agent)}
                  className="flex-1 bg-black/5 dark:bg-white/5 py-2 rounded-xl text-sm font-semibold active:bg-black/10"
                >
                  Редактировать
                </button>
                <button
                  onClick={() => handleDelete(agent.id)}
                  className="flex-1 bg-red-500/10 text-red-500 py-2 rounded-xl text-sm font-semibold active:bg-red-500/20"
                >
                  Удалить
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
